import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, Mail, AlertTriangle, Check } from "lucide-react";
import { fetchSentimentTicket } from "@/lib/api";
import type { SentimentEmail } from "@/lib/sentiment-types";

interface TicketDrilldownProps {
  ticketId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const severityBadge = (severity: number) => {
  const colors: Record<number, string> = {
    1: "bg-green-100 text-green-800",
    2: "bg-yellow-100 text-yellow-800",
    3: "bg-orange-100 text-orange-800",
    4: "bg-red-100 text-red-800",
    5: "bg-red-200 text-red-900",
  };
  return colors[severity] || "bg-gray-100 text-gray-800";
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  } catch {
    return "-";
  }
};

function EmailCard({ email }: { email: SentimentEmail }) {
  const result = email.analysisResult;
  const hasComplaint = result?.hasComplaint;

  return (
    <div className={`border rounded-lg p-4 ${hasComplaint ? "border-red-200 bg-red-50/50" : "border-gray-200"}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mail className={`h-4 w-4 ${email.isInbound ? "text-blue-500" : "text-gray-400"}`} />
          <span className="text-sm font-medium">
            {email.isInbound ? "Inbound" : "Outbound"}
          </span>
          {email.isAnalysed && (
            hasComplaint ? (
              <Badge className="bg-red-500 text-white text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Severity {result?.severity}/5
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-green-700 border-green-300">
                <Check className="h-3 w-3 mr-1" />
                No complaint
              </Badge>
            )
          )}
          {!email.isAnalysed && email.isInbound && (
            <Badge variant="outline" className="text-xs">Pending analysis</Badge>
          )}
        </div>
        <span className="text-xs text-gray-500">{formatDate(email.engagementTimestamp)}</span>
      </div>

      <div className="text-sm mb-1">
        <span className="text-gray-500">From:</span>{" "}
        {email.senderName || email.senderEmail || "Unknown"}
      </div>
      {email.subject && (
        <div className="text-sm mb-2">
          <span className="text-gray-500">Subject:</span> {email.subject}
        </div>
      )}

      {result?.hasComplaint && (
        <div className="mt-3 pt-3 border-t border-red-200">
          <div className="flex flex-wrap gap-1 mb-2">
            {result.categories.map((cat) => (
              <Badge key={cat} variant="outline" className="text-xs">
                {cat.replace("_", " ")}
              </Badge>
            ))}
          </div>
          {result.summary && (
            <p className="text-sm text-gray-700 mb-1">{result.summary}</p>
          )}
          {result.evidence && (
            <p className="text-xs text-gray-500 italic">"{result.evidence}"</p>
          )}
        </div>
      )}
    </div>
  );
}

export function TicketDrilldown({ ticketId, open, onOpenChange }: TicketDrilldownProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["sentimentTicket", ticketId],
    queryFn: () => fetchSentimentTicket(ticketId!),
    enabled: !!ticketId && open,
  });

  const ticket = data?.ticket;
  const emails = data?.emails || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {isLoading ? (
              <Skeleton className="h-6 w-64" />
            ) : (
              <>
                {ticket?.subject || ticketId}
                {ticket?.hubSpotUrl && (
                  <a href={ticket.hubSpotUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                  </a>
                )}
              </>
            )}
          </SheetTitle>
          <SheetDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-48" />
            ) : (
              <div className="flex gap-4 text-sm">
                <span>{ticket?.totalEmailCount || 0} emails</span>
                <span>{ticket?.inboundEmailCount || 0} inbound</span>
                <span>{ticket?.complaintCount || 0} complaints</span>
                {ticket?.titleReference && <span>Ref: {ticket.titleReference}</span>}
              </div>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))
          ) : emails.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No emails found for this ticket.</div>
          ) : (
            emails.map((email) => <EmailCard key={email.emailId} email={email} />)
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
